import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import doneIcon from '@/assets/checked.svg';
import CardItemView from '@/components/CardItemView';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const OrderSuccessSection = ({ orderData, relatedCards = [] }) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen w-full bg-white flex flex-col items-center py-10 px-4">
      <div className="relative w-[250px] h-[350px]" style={{ transformStyle: "preserve-3d", perspective: 1000 }} >
        <motion.div
          className="w-full h-full"
          initial={{ 
            y: 300,
            rotateY: 0,
            scale: 1.2
          }}
          animate={{ 
            y: [300, 0, 0],
            rotateY: [0, 360, 360],
            scale: [1.2, 1.2, 1]
          }}
          transition={{
            duration: 1.5,
            times: [0, 0.8, 1],
            ease: 'easeInOut',
          }}
          style={{ transformStyle: "preserve-3d", perspective: 1000 }}
        >
          {/* Front side */}
          <div 
            className="absolute w-full h-full rounded-lg overflow-hidden"
            style={{ backfaceVisibility: "hidden" }}
          >
            <img 
              src={getProductThumbnailUrl(orderData.image)} 
              alt={orderData.title || t('purchase.success.product_image')} 
              className="w-full h-full object-contain"
            />
            
            {/* Shine effect overlay */}
            <motion.div 
              className="absolute z-10"
              initial={{ opacity: 0.4 }}
              animate={{ 
                x: ["-50%", "50%"],
                y: ["-50%", "-150%"]
              }}
              transition={{
                delay: 1.6, // Start after card animation
                duration: 1,
                times: [0, 1],
                ease: "easeOut"
              }}
              style={{
                background: "linear-gradient(45deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 65%, rgba(255,255,255,0) 100%)",
                width: "120%",
                height: "120%",
                pointerEvents: "none"
              }}
            />
          </div>
          
          {/* Back side */}
          <div 
            className="absolute w-full h-full rounded-lg overflow-hidden"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <img 
              src={getProductThumbnailUrl(orderData.backImage || orderData.image)} 
              alt={t('purchase.success.product_back')} 
              className="w-full h-full object-contain"
            />
          </div>
        </motion.div>
      </div>
      
      <motion.div 
        className="max-w-md w-full text-center mt-8"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 1.2,
          duration: 0.3
        }}
      >
        <div className="flex items-center justify-center mb-4">
          <img src={doneIcon} alt="done" className="w-7 h-7 mr-1.5" />
          <h1 className="text-xl font-bold text-black">
            {t('purchase.success.title', 'Order Successful')}
          </h1>
        </div>
        
        <p className="text-sm text-gray-600 mb-6">
          {t('purchase.success.shipping_time', '5~7 business days shipping')}
        </p>
      </motion.div>

      {/* Related Cards Section */}
      {relatedCards && relatedCards.length > 0 && (
        <motion.div 
          className="container mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 1.8,
            duration: 0.4
          }}
        >
          <h2 className="text-xl font-bold mb-6">
            {t('purchase.success.related_recommendations')}
          </h2>
          <div className="grid grid-cols-5 gap-6">
            {relatedCards.map((card) => (
              <CardItemView
                key={card.id}
                id={card.id}
                name={card.localizedRawName}
                type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                image={card.images && card.images.length > 0 ? card.images[0].url : ""}
                brand={card.brand?.localizedName}
                price={card.stats?.lowestPriceOnSale || 0}
                orders={card.stats?.salesVolume}
                code={card.type === 'CARD_BOX' ? card.set?.code : (card.set?.code && card.rawCode ? `${card.set.code} ${card.rawCode}` : card.set?.code || card.rawCode)}
                set={card.set}
                rawCode={card.rawCode}
                rarityInfo={card.rarityInfo}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default OrderSuccessSection; 
